import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { MainLayout } from '@/components/layout';
import { usersApi } from '@/api';

export function CreateUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await usersApi.createUser({ email, name });
      enqueueSnackbar(t('admin.createSuccess'), { variant: 'success' });
      navigate('/admin/users');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.serverError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout maxWidth="sm">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('admin.createUser')}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('auth.email')}
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label={t('user.name')}
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('activation.requestDescription')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/users')}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !email || !name}
            >
              {loading ? t('common.loading') : t('common.create')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </MainLayout>
  );
}
